import React from "react"
import {IconButton, IconTypes} from "@irati/ui/src/Button"
import {Input, Select} from "@irati/ui/src/FormItem"
import validation from "@irati/utils/validation"
import {injectIntl} from "@irati/utils/lang"
import {EditableBudget, SimpleTag, useBudgetManagement, useTags} from "../hooks"
import {ColorPicker} from "../shared/ColorPicker"
import {Item, Row} from "./EditableRow"
import {AmountInput} from "./AmountInput"

interface Props {
  max: number
  invalidNames: string[]
  remove?: () => void
}

const WORDY = /^[\w' -]+$/
const MAX_BUDGET_NAME_LENGTH = 30

export const Budget = injectIntl<Props & EditableBudget>(({remove, invalidNames, formatMessage, ...category}) => {
  const tags = useTags()
  const {dispatch} = useBudgetManagement()
  const {uuid, metadata, name} = category
  const tag = tags?.find((t) => category.tag?.uuid === t.uuid)

  return (
    <Row>
      <Item area="icon">
        <ColorPicker
          value={metadata.color}
          onChange={(c) => {
            dispatch({type: "setColor", data: {uuid, value: c}})
          }}
        />
      </Item>

      <Item area="name">
        <Input
          label="name"
          hideLabel
          className="flex-override flex-input-override"
          name={`${uuid}-name`}
          placeholder="wallet.reward.budget.dashboard.placeholder.name"
          validation={validation
            .string("platform.defaultValidation.required")
            .max(MAX_BUDGET_NAME_LENGTH, formatMessage({id: "platform.defaultValidation.maxLength"}, {count: MAX_BUDGET_NAME_LENGTH}))
            .test(
              "notJustSpaces",
              formatMessage({id: "platform.defaultValidation.notJustSpaces"}),
              (value) => value && value.trim().length !== 0
            )
            .matches(WORDY, formatMessage({id: "wallet.reward.budget.dashboard.validation.invalidChar"}))
            .notOneOf(invalidNames, "platform.defaultValidation.alreadyAdded")}
          value={name}
          onChange={(n) => {
            dispatch({type: "setName", data: {uuid, value: n}})
          }}
        />
      </Item>

      <Item area="tag">
        <Select
          name="tag"
          label="tag"
          placeholder="wallet.reward.budget.dashboard.placeholder.tag"
          hideLabel
          className={"flex-override flex-select-override"}
          onChange={(t) => {
            dispatch({type: "setTag", data: {uuid, value: t as unknown as SimpleTag}})
          }}
          validation={validation.object("platform.defaultValidation.selectOne")}
          value={tag ? {value: tag, label: tag.name} : undefined}
          options={tags?.map((t) => ({value: t, label: t.name})) ?? []}
        />
      </Item>

      <Item area="amount">
        <AmountInput {...category} formatMessage={formatMessage} />
      </Item>

      {remove && (
        <Item area="menu" direction="column" justifyContent="center">
          <IconButton icon={IconTypes.delete} onClick={remove} extraLarge color={"black"} />
        </Item>
      )}
    </Row>
  )
})
